import { ReactComponent as Logo } from "../../vendor/logo_black.svg";
import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Alert as MuiAlert,
  TextField as MuiTextField,
  Paper,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Formik } from "formik";
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import * as Yup from "yup";

const Alert = styled(MuiAlert)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Brand = styled(Logo)`
  fill: ${(props) => props.theme.palette.primary.main};
  width: 250px;
  margin-bottom: 32px;
`;

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)};

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)};
  }
`;

const ForgotPassword = () => {
  return (
    <>
      <Brand />
      <Wrapper>
        <Helmet title="Forgot Password" />

        <ForgotPasswordForm />
      </Wrapper>
    </>
  );
};

const ForgotPasswordForm = () => {
  const SEND_PASSWORD_RESET_EMAIL_MUTATION = gql`
    mutation SendPasswordResetEmail($email: String!) {
      sendPasswordResetEmail(email: $email) {
        success
        errors
      }
    }
  `;

  const [sendPasswordResetEmail, { data, loading, error }] = useMutation(
    SEND_PASSWORD_RESET_EMAIL_MUTATION
  );

  return (
    <>
      {loading && (
        <Alert my={2} severity="info">
          Handling your request...
        </Alert>
      )}
      {error && (
        <Alert my={2} severity="warning">
          Sorry, there was an error. Please contact us at{" "}
          <a href="mailto:support@podium.live">support@podium.live</a>.
        </Alert>
      )}
      {data && (
        <Alert my={2} severity="success">
          Please check your inbox for an email that has the next steps to reset
          your password.
        </Alert>
      )}

      <Typography component="h2" variant="body1" align="center">
        Enter your email address to reset your password
      </Typography>

      <Formik
        initialValues={{
          email: "",
          submit: false,
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email("Must be a valid email")
            .max(255)
            .required("Email is required"),
        })}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            await sendPasswordResetEmail({
              variables: { email: values.email },
            });
          } catch (error) {
            const message = error.message || "Something went wrong";

            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <TextField
              autoFocus
              type="email"
              name="email"
              label="Email Address"
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
              my={3}
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              Reset password
            </Button>
            <Button
              component={Link}
              to="/auth/sign-in"
              fullWidth
              color="primary"
            >
              Cancel
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default ForgotPassword;
